import { extractReferralSourceFromPath } from "./referralLinks";

export const UTM_STORAGE_PREFIX = "pm_utm_";
export const UTM_LANDING_KEY = "pm_utm_landing";
export const UTM_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
export const LANDING_ATTRIBUTION_KEYS = ["tracker", "aclid"];

function safeGet(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch (_) {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch (_) {}
}

function safeRemove(storage, key) {
  try {
    storage?.removeItem(key);
  } catch (_) {}
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, maxAgeMs) {
  if (typeof document === "undefined") return;
  const maxAgeSeconds = Math.max(0, Math.floor(maxAgeMs / 1000));
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
}

function clearCookie(name) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function tryParseJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function emptyLandingPayload() {
  return [...UTM_KEYS, ...LANDING_ATTRIBUTION_KEYS].reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});
}

function persistLegacyUtm(param, value) {
  if (typeof window === "undefined") return;
  if (value == null || value === "") return;
  const key = `${UTM_STORAGE_PREFIX}${param}`;
  safeSet(window.localStorage, key, value);
  safeSet(window.sessionStorage, key, value);
}

function readLegacyUtmPayload() {
  if (typeof window === "undefined") return null;
  const payload = emptyLandingPayload();
  let hasAny = false;

  UTM_KEYS.forEach((param) => {
    const key = `${UTM_STORAGE_PREFIX}${param}`;
    const stored =
      safeGet(window.localStorage, key) ?? safeGet(window.sessionStorage, key);
    if (stored != null && stored !== "") {
      payload[param] = stored;
      hasAny = true;
    }
  });

  if (!hasAny) return null;
  return { ...payload, first_seen_at: new Date().toISOString() };
}

function clearLegacyUtms() {
  if (typeof window === "undefined") return;
  UTM_KEYS.forEach((param) => {
    const key = `${UTM_STORAGE_PREFIX}${param}`;
    safeRemove(window.localStorage, key);
    safeRemove(window.sessionStorage, key);
  });
}

export function readLandingData() {
  if (typeof window === "undefined") return null;
  const rawLocal = safeGet(window.localStorage, UTM_LANDING_KEY);
  if (rawLocal) {
    const parsed = tryParseJson(rawLocal);
    if (parsed && typeof parsed === "object") {
      if (parsed.expires_at && Date.now() > parsed.expires_at) {
        safeRemove(window.localStorage, UTM_LANDING_KEY);
      } else if (parsed.value && typeof parsed.value === "object") {
        return parsed.value;
      } else if (
        parsed.utm_source !== undefined ||
        parsed.tracker !== undefined ||
        parsed.aclid !== undefined ||
        parsed.first_seen_at
      ) {
        return parsed;
      }
    }
  }

  const rawCookie = readCookie(UTM_LANDING_KEY);
  if (!rawCookie) return null;
  const parsedCookie = tryParseJson(rawCookie);
  if (parsedCookie && typeof parsedCookie === "object") {
    if (parsedCookie.value && typeof parsedCookie.value === "object") {
      return parsedCookie.value;
    }
    return parsedCookie;
  }

  return null;
}

function isLandingExpired(landing) {
  if (!landing || !landing.first_seen_at) return false;
  const timestamp = Date.parse(landing.first_seen_at);
  if (Number.isNaN(timestamp)) return false;
  return Date.now() - timestamp > UTM_TTL_MS;
}

function clearLandingStorage() {
  if (typeof window === "undefined") return;
  safeRemove(window.localStorage, UTM_LANDING_KEY);
  clearCookie(UTM_LANDING_KEY);
}

function writeLandingData(landing) {
  if (typeof window === "undefined") return;
  const record = { ...landing };
  const wrapped = JSON.stringify({
    value: record,
    expires_at: Date.now() + UTM_TTL_MS,
  });
  safeSet(window.localStorage, UTM_LANDING_KEY, wrapped);
  writeCookie(UTM_LANDING_KEY, JSON.stringify(record), UTM_TTL_MS);
  UTM_KEYS.forEach((param) => persistLegacyUtm(param, record[param]));
}

function resolveLocationParts(locationLike) {
  if (locationLike && typeof locationLike === "object") {
    return {
      pathname: locationLike.pathname || "",
      search: locationLike.search || "",
    };
  }
  if (typeof window === "undefined") {
    return { pathname: "", search: "" };
  }
  return {
    pathname: window.location?.pathname || "",
    search: window.location?.search || "",
  };
}

function readUtmParamsFromLocation(locationLike) {
  const values = {};
  let hasAny = false;
  const { pathname, search } = resolveLocationParts(locationLike);

  try {
    const params = new URLSearchParams(search || "");
    UTM_KEYS.forEach((param) => {
      if (params.has(param)) {
        values[param] = params.get(param) || "";
        hasAny = true;
      }
    });
    if (!values.utm_source) {
      const referralSource = extractReferralSourceFromPath(pathname);
      if (referralSource) {
        values.utm_source = referralSource;
        hasAny = true;
      }
    }
  } catch (_) {}
  return { values, hasAny };
}

function readLandingAttributionFromLocation(locationLike) {
  const values = {};
  let hasAny = false;
  const { search } = resolveLocationParts(locationLike);

  try {
    const params = new URLSearchParams(search || "");
    LANDING_ATTRIBUTION_KEYS.forEach((param) => {
      if (!params.has(param)) return;
      values[param] = params.get(param) || "";
      hasAny = true;
    });
  } catch (_) {}
  return { values, hasAny };
}

export function cacheCurrentUTMs(locationLike) {
  if (typeof window === "undefined") return null;
  const nowIso = new Date().toISOString();
  const empty = emptyLandingPayload();
  let landing = readLandingData();
  let landingExpired = false;

  if (landing && isLandingExpired(landing)) {
    clearLandingStorage();
    clearLegacyUtms();
    landing = null;
    landingExpired = true;
  }

  if (!landing && !landingExpired) {
    landing = readLegacyUtmPayload();
  }

  const { values: urlValues, hasAny: hasUrlUtms } =
    readUtmParamsFromLocation(locationLike);
  const { values: attributionValues, hasAny: hasAttributionValues } =
    readLandingAttributionFromLocation(locationLike);

  if (!landing) {
    const initial = {
      ...empty,
      ...urlValues,
      ...attributionValues,
      first_seen_at: nowIso,
    };
    writeLandingData(initial);
    return initial;
  }

  const normalized = { ...empty, ...landing };
  let shouldPersist = false;

  if (!normalized.first_seen_at) {
    normalized.first_seen_at = nowIso;
    shouldPersist = true;
  }

  if ([...UTM_KEYS, ...LANDING_ATTRIBUTION_KEYS].some((key) => landing[key] == null)) {
    shouldPersist = true;
  }

  if (hasUrlUtms) {
    const next = { ...empty, ...urlValues };
    const changed = UTM_KEYS.some(
      (key) => (next[key] ?? "") !== (normalized[key] ?? "")
    );
    if (changed) {
      const updated = { ...normalized, ...next, first_seen_at: nowIso };
      writeLandingData(updated);
      return updated;
    }
  }

  if (hasAttributionValues) {
    const changed = LANDING_ATTRIBUTION_KEYS.some(
      (key) => (attributionValues[key] ?? "") !== (normalized[key] ?? "")
    );
    if (changed) {
      const updated = { ...normalized, ...attributionValues };
      writeLandingData(updated);
      return updated;
    }
  }

  if (shouldPersist) {
    writeLandingData(normalized);
  }

  return normalized;
}
